import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowRight, CheckCircle, Paintbrush, Home as HomeIcon, Building, Store, Castle, Droplets } from 'lucide-react';
import { Button } from '../components/ui/button';
import ReviewCarousel from '../components/ReviewCarousel';
import SEO from '../components/SEO';
import JsonLd from '../components/JsonLd';
import { servicesSchema } from '../lib/schema';
import { services } from '../data/mockData';

const Services = () => {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const element = document.getElementById(location.hash.slice(1));
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    }
  }, [location]);

  const servicesSummary = [
    { name: "Interior", slug: "interior-painting", icon: Paintbrush },
    { name: "Exterior", slug: "exterior-painting", icon: HomeIcon },
    { name: "Landlords", slug: "landlords-rental-properties", icon: Building },
    { name: "Commercial", slug: "offices-shops", icon: Store },
    { name: "Period Homes", slug: "period-properties", icon: Castle },
    { name: "Roof & Drive Cleaning", slug: "pressure-washing", icon: Droplets }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="Interior & Exterior Painters in Kent | Paint It Better"
        description="Professional painting and decorating services in Kent. Interior, exterior, landlord refreshes and commercial work. Quality finish, fair prices, 10/10 reviews."
        canonical="/services"
      />
      <JsonLd data={servicesSchema(services)} />

      {/* Hero */}
      <section className="relative py-24 bg-gradient-to-br from-slate-900 via-slate-800 to-cyan-900 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Painting & Decorating Services
            </h1>
            <p className="text-xl text-slate-300 leading-relaxed">
              Professional painting and decorating for homes, landlords and businesses across Wye, Ashford, Canterbury and Kent.
            </p>
          </div>
        </div>
      </section>

      {/* Quick Nav */}
      <section className="py-4 bg-slate-50 border-b sticky top-20 z-30">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap justify-center gap-2">
            {servicesSummary.map((s) => (
              <a
                key={s.slug}
                href={`#${s.slug}`}
                className="inline-flex items-center px-4 py-2 rounded-full bg-white hover:bg-cyan-50 text-slate-700 hover:text-cyan-700 text-sm font-medium transition-colors border border-slate-200 hover:border-cyan-300"
              >
                <s.icon className="h-4 w-4 mr-2" />
                {s.name}
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Interior Painting */}
      <ServiceSection
        id="interior-painting"
        icon={Paintbrush}
        label="Interior Decorating"
        title="Interior Painting"
        image="/images/emergent/interior-kitchen.jpg"
        imageAlt="Beautifully painted barn conversion kitchen in Kent with sage green cabinets, exposed oak beams and vaulted ceiling"
        description="Whether you're refreshing a single room or redecorating your entire home, we bring the same care and attention to every job. We handle preparation, protect your furniture and floors, and work efficiently to minimise disruption."
        features={[
          "Surface preparation – filling, sanding, priming",
          "Protection for floors, furniture and fixtures",
          "High-quality emulsion for walls and ceilings",
          "Woodwork – skirting, door frames, doors",
          "Clean and tidy work area throughout"
        ]}
        links={[
          { text: "How much does it cost?", to: "/blog/how-much-does-it-cost-to-paint-a-room-in-kent-2026" },
          { text: "Read our FAQ", to: "/faq" }
        ]}
        reverse={false}
      />

      {/* Exterior Painting */}
      <ServiceSection
        id="exterior-painting"
        icon={HomeIcon}
        label="Exterior Decorating"
        title="Exterior Painting"
        image="/images/portfolio/project_01.jpg"
        imageAlt="Professional exterior house painting showing freshly painted walls and trim in Kent"
        description="Kent's weather can be hard on exterior paintwork. We prepare surfaces properly and use the right products so your exterior lasts. We also paint fences, gates, garden cabins and outbuildings."
        features={[
          "Thorough surface preparation",
          "Treatment for algae, mould and bare timber",
          "Weather-resistant primers and top coats",
          "Scaffolding coordination where needed",
          "Fences, cabins and garden structures"
        ]}
        links={[
          { text: "How long does exterior paint last?", to: "/blog/exterior-house-painting-kent-how-long-does-it-last" },
          { text: "See our projects", to: "/portfolio" }
        ]}
        reverse={true}
        bgColor="bg-slate-50"
      />

      {/* Landlords */}
      <ServiceSection
        id="landlords-rental-properties"
        icon={Building}
        label="Rental & HMO Decorating"
        title="Landlords & Rental Properties"
        image="/images/portfolio/project_18.jpg"
        imageAlt="Freshly decorated rental property interior ready for new tenants"
        description="Void periods are expensive. We provide fast, reliable between-tenant refreshes for portfolio landlords and managing agents. Fixed dates, no surprises, plus photo and video updates if you can't be on site."
        features={[
          "Fast turnaround to minimise voids",
          "Fixed dates and reliable scheduling",
          "Photo and video updates for remote landlords",
          "Communal areas and blocks of flats",
          "Durable, easy-clean finishes"
        ]}
        links={[
          { text: "Landlord's guide", to: "/blog/landlords-guide-between-tenant-refreshes-kent" },
          { text: "Get a quote", to: "/contact" }
        ]}
        reverse={false}
      />

      {/* Offices & Shops */}
      <ServiceSection
        id="offices-shops"
        icon={Store}
        label="Commercial Decorating"
        title="Offices & Shops"
        image="/images/portfolio/project_02.jpg"
        imageAlt="Professionally painted office interior with clean white walls"
        description="A tired workspace drags down staff morale and customer confidence. We plan work around your opening hours so you don't have to shut the doors, protecting stock and tidying up at the end of each day."
        features={[
          "Work planned around trading hours",
          "Protection for stock and equipment",
          "Durable finishes for high-traffic areas",
          "Out-of-hours work by arrangement",
          "Clean and professional throughout"
        ]}
        links={[
          { text: "Commercial painting in Kent", to: "/commercial-painting-kent" },
          { text: "How we work in commercial spaces", to: "/blog/office-shop-repaints-without-shutting-doors" },
          { text: "Discuss your project", to: "/contact" }
        ]}
        reverse={true}
        bgColor="bg-slate-50"
      />

      {/* Period Properties */}
      <ServiceSection
        id="period-properties"
        icon={Castle}
        label="Heritage & Character Homes"
        title="Period Properties"
        image="/images/emergent/period-tudor.jpg"
        imageAlt="Tudor-style period property in Kent with black timber framing, brick exterior and traditional leaded windows"
        description="Kent has beautiful period homes that need careful handling. We're experienced with sash windows, exposed beams, lime-based surfaces and listed buildings. We use breathable paints and take extra care around original features."
        features={[
          "Sash and timber window painting",
          "Exposed beams and original woodwork",
          "Breathable and heritage-appropriate paints",
          "Careful treatment of lime plaster",
          "Coordination with specialist joiners"
        ]}
        links={[
          { text: "Sash window repair or replace?", to: "/blog/sash-window-timber-painting-kent-repair-or-replace" },
          { text: "See period projects", to: "/portfolio" }
        ]}
        reverse={false}
      />

      {/* Clearing & Pressure Cleaning */}
      <ServiceSection
        id="pressure-washing"
        icon={Droplets}
        label="Roof, Drive & Decking Cleaning"
        title="Clearing & Pressure Cleaning"
        image="/images/emergent/pressure-cleaning.jpg"
        imageAlt="Before and after roof pressure washing showing moss removal on tiled roof"
        description="Roofs, driveways and decking build up moss, algae and dirt over time. Roof cleaning is often the single biggest visual improvement you can make. We assess each surface and use controlled, careful methods."
        features={[
          "Roof cleaning on tiled and slate roofs",
          "Driveways and block paving",
          "Patios, paths, steps and hard-standing",
          "Decking and wooden terraces",
          "Walls and garden features"
        ]}
        links={[
          { text: "Why roof cleaning gives the biggest change", to: "/blog/why-roof-pressure-washing-gives-biggest-visual-change" },
          { text: "Get a quote", to: "/contact" }
        ]}
        reverse={true}
        bgColor="bg-slate-50"
      />

      {/* Checkatrade Banner */}
      <section className="py-12 bg-cyan-600">
        <div className="container mx-auto px-4 text-center">
          <p className="text-white text-lg">
            See our reviews and project photos on{' '}
            <a 
              href="https://www.checkatrade.com/trades/PaintItBetterLtd" 
              target="_blank" 
              rel="noopener"
              className="font-bold underline underline-offset-2 hover:text-cyan-100"
            >
              Checkatrade →
            </a>
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-slate-900 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Discuss Your Project?
          </h2>
          <p className="text-xl mb-8 text-slate-300 max-w-2xl mx-auto">
            Get in touch for a free, no-obligation quote.
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-cyan-500 hover:bg-cyan-600 text-white px-10 py-6 text-lg rounded-full">
              Request a Quote
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>

      <ReviewCarousel />
    </div>
  );
};

// Reusable Service Section Component
const ServiceSection = ({ id, icon: Icon, label, title, image, imageAlt, description, features, links, reverse = false, bgColor = "bg-white" }) => {
  return (
    <section id={id} className={`py-16 md:py-24 ${bgColor} scroll-mt-32`}>
      <div className="container mx-auto px-4">
        <div className={`max-w-6xl mx-auto grid lg:grid-cols-2 gap-8 md:gap-12 items-center ${reverse ? 'lg:flex-row-reverse' : ''}`}>
          {/* Image */}
          <div className={`${reverse ? 'lg:order-1' : 'lg:order-2'}`}>
            <div className="relative rounded-2xl overflow-hidden shadow-2xl aspect-[4/3]">
              <img 
                src={image} 
                alt={imageAlt}
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          {/* Content */}
          <div className={`${reverse ? 'lg:order-2' : 'lg:order-1'}`}>
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-cyan-100 rounded-lg">
                <Icon className="h-5 w-5 text-cyan-600" />
              </div>
              <span className="text-cyan-600 font-medium text-sm">{label}</span>
            </div>
            
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              {title}
            </h2>
            
            <p className="text-slate-600 text-lg leading-relaxed mb-6">
              {description}
            </p>

            <ul className="space-y-2 mb-6">
              {features.map((item, idx) => (
                <li key={idx} className="flex items-start gap-2 text-slate-600">
                  <CheckCircle className="h-5 w-5 text-cyan-500 flex-shrink-0 mt-0.5" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>

            <div className="flex flex-wrap gap-4">
              {links.map((link, idx) => (
                <Link 
                  key={idx} 
                  to={link.to} 
                  className="text-cyan-600 hover:text-cyan-700 font-medium underline underline-offset-2"
                >
                  {link.text}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Services;
